import React, { useState } from 'react';
import { TwIconButton } from 'components/common';
import PropTypes from 'prop-types';
import { FaUsers, FaDownload, FaEye, FaCheck, FaPen, FaLink, FaTrash } from 'react-icons/fa';
import { getFileMeta } from './fileTypes';
import { useFolderColor } from './folderColorStore';
import { getSize } from 'helpers/utils';

const DriveListItem = ({ item, index, isSelected, onToggleSelect, onOpen, handleAction }) => {
  const { kind, icon: Icon, color: defaultColor } = getFileMeta(item);
  const isFolder = kind === 'folder';
  const isImage  = kind === 'image';
  const folderColor = useFolderColor(isFolder ? item.id : null, isFolder ? index : undefined);
  const useFolderTint = isFolder && folderColor;
  const color = useFolderTint ? folderColor.icon : defaultColor;
  const iconBg = useFolderTint ? folderColor.tint : '#f8fafc';
  const iconBorder = useFolderTint ? folderColor.border : '#f1f5f9';
  const [imgError, setImgError] = useState(false);
  const [hover, setHover] = useState(false);

  const { allow_view, allow_edit, allow_download, allow_move_to_trash, allow_share } = item.permissions || {};

  const handleRowClick = (e) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onToggleSelect(item);
      return;
    }
    onOpen(item);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    handleAction(item, 'detail');
  };

  return (
    <div
      onClick={handleRowClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
        borderBottom: '1px solid #f1f5f9',
        backgroundColor: isSelected ? '#eff6ff' : (hover ? '#fafbfc' : '#ffffff'),
        cursor: 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      {/* Custom checkbox — inline styled, immune to Bootstrap */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect(item); }}
        aria-label={isSelected ? 'Deselect' : 'Select'}
        style={{
          appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
          width: 18, height: 18, padding: 0, margin: 0, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: isSelected ? '#2563eb' : '#ffffff',
          color: isSelected ? '#ffffff' : 'transparent',
          border: `1.5px solid ${isSelected ? '#2563eb' : '#cbd5e1'}`,
          borderRadius: 4, cursor: 'pointer',
          transition: 'background-color 120ms ease, border-color 120ms ease',
        }}
      >
        {isSelected && <FaCheck size={9} />}
      </button>

      {/* Icon / Thumb */}
      <div style={{
        width: 36, height: 36, flexShrink: 0,
        backgroundColor: iconBg, border: `1px solid ${iconBorder}`,
        borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}>
        {isImage && item.preview_url && !imgError ? (
          <img
            src={item.preview_url} alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy" onError={() => setImgError(true)}
          />
        ) : (
          <Icon size={16} style={{ color }} />
        )}
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <p
            style={{
              margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }}
            title={item.name}
          >
            {item.name}
          </p>
          {item.public && <FaUsers size={10} style={{ color: '#94a3b8', flexShrink: 0 }} />}
        </div>
        <p style={{
          margin: '2px 0 0', fontSize: 11, fontWeight: 500, color: '#94a3b8',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {isFolder ? 'Folder' : (item.content_type || 'File')}
        </p>
      </div>

      {/* Owner */}
      <div
        className="tw-hidden md:tw-block"
        style={{
          width: 144, fontSize: 12, color: '#64748b', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {item.created_by || '—'}
      </div>

      {/* Modified */}
      <div
        className="tw-hidden lg:tw-block"
        style={{
          width: 112, fontSize: 11, color: '#64748b',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {item.updated_date || '—'}
      </div>

      {/* Size */}
      <div
        className="tw-hidden sm:tw-block"
        style={{
          width: 80, textAlign: 'right',
          fontSize: 11, color: '#64748b', fontWeight: 500,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          flexShrink: 0,
        }}
      >
        {isFolder ? '—' : getSize(item.file_size || 0)}
      </div>

      {/* Actions — fade in on row hover. Right-click for more actions. */}
      <div
        style={{
          width: 156, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4,
          opacity: hover ? 1 : 0,
          transition: 'opacity 150ms ease',
          pointerEvents: hover ? 'auto' : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!isFolder && allow_view && (
          <TwIconButton icon={FaEye}        size="sm" variant="secondary" onClick={() => handleAction(item, 'preview')}   title="Preview" />
        )}
        {allow_download && (
          <TwIconButton icon={FaDownload}   size="sm" variant="secondary" onClick={() => handleAction(item, 'download')}  title="Download" />
        )}
        {allow_share && (
          <TwIconButton icon={FaLink}       size="sm" variant="secondary" onClick={() => handleAction(item, 'getLink')}   title="Get link" />
        )}
        {allow_edit && (
          <TwIconButton icon={FaPen}        size="sm" variant="secondary" onClick={() => handleAction(item, 'rename')}    title="Rename" />
        )}
        {allow_move_to_trash && (
          <TwIconButton icon={FaTrash}      size="sm" variant="danger"    onClick={() => handleAction(item, 'moveToTrash')} title="Move to trash" />
        )}
      </div>
    </div>
  );
};

DriveListItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  handleAction: PropTypes.func.isRequired,
};

export default DriveListItem;
